import { useState } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import {
  Menu,
  X,
  Home,
  Bot,
  Target,
  Settings,
  LogOut,
  User,
  Building,
  Users,
  Sparkles,
  Link2,
  ChevronDown,
  FileText,
  TrendingUp,
  CheckCircle,
  Image,
  Library,
} from 'lucide-react'

import { useAuthStore } from '@/store/auth'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/Button'
import { AgencySelector } from '@/components/agencies/AgencySelector'
import { BrandSelector } from '@/components/brands/BrandSelector'
import { ConnectionStatus } from '@/components/realtime/ConnectionStatus'
import { NotificationBell, NotificationCenter } from '@/components/realtime/NotificationCenter'
import { NotificationToast } from '@/components/realtime/NotificationToast'
import { DecisionBadge } from '@/components/decisions/DecisionQueue'
import { FeedbackWidget } from '@/components/FeedbackWidget'
import { TierBadge, QuotaWarningBanner, QuotaWarningModal, QuotaExceededPaywall } from '@/components/billing'
import { BetaFeedbackModal } from '@/components/beta/BetaFeedbackModal'
import { useAgency } from '@/contexts/AgencyContext'
import { useBrand } from '@/contexts/BrandContext'
import { useTierData } from '@/hooks/useTierData'
import { useBetaFeedback } from '@/hooks/useBetaFeedback'

export function AppLayout() {
  const location = useLocation()
  const { user } = useAuthStore()
  const { logout, userId } = useAuth() // Use Clerk's logout
  const { selectedAgency } = useAgency()
  const { selectedBrand } = useBrand()
  const { data: tierData } = useTierData()
  const { shouldShowModal: shouldShowBetaFeedback, dismissModal: dismissBetaFeedback } = useBetaFeedback(user?.id || null)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)

  // Track expanded submenus
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({
    brands: true,
    'audience-cdp': false,
    orchestration: false,
    campaigns: false,
    'agents-&-automation': false,
    analytics: false,
    'ai-intelligence': false,
  })

  // Helper to check if path matches
  const isPathActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/')

  // Main navigation - Consolidated from 33 items to 8
  const primaryNavigation = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: Home,
      current: location.pathname === '/dashboard',
    },
    {
      name: 'Brands',
      href: '/brands',
      icon: Sparkles,
      current: isPathActive('/brands') || isPathActive('/competitive-intel'),
      // Multi-Brand Dashboard and Comparative Analytics deferred to Q1 2026
      // Routes remain active at /multi-brand and /analytics/comparative but not exposed in navigation
    },
    {
      name: 'Audience',
      href: '/customers',
      icon: Users,
      current: isPathActive('/customers') || isPathActive('/segments'),
    },
    {
      name: 'Campaigns',
      icon: Target,
      current: isPathActive('/campaigns') || isPathActive('/workflows') || isPathActive('/abtests') || isPathActive('/briefs') || isPathActive('/creatives') || isPathActive('/content'),
      submenu: [
        { name: 'All Campaigns', href: '/campaigns', icon: Target },
        { name: 'Content Library', href: '/content', icon: Library },
        { name: 'Creatives', href: '/creatives', icon: Image },
        { name: 'Briefs', href: '/briefs', icon: FileText },
      ],
    },
    {
      name: 'Agents',
      href: '/agents',
      icon: Bot,
      current: isPathActive('/agents') || isPathActive('/orchestration') || isPathActive('/executions') || isPathActive('/conflicts') || isPathActive('/realtime') || isPathActive('/ml-predictions') || isPathActive('/recommendations'),
    },
    {
      name: 'Approvals',
      href: '/approvals',
      icon: CheckCircle,
      current: isPathActive('/approvals'),
      badge: 'new',
    },
    {
      name: 'Analytics & Insights',
      href: '/analytics',
      icon: TrendingUp,
      current: isPathActive('/analytics') || isPathActive('/budget') || isPathActive('/attribution') || isPathActive('/reports'),
    },
  ]

  // Secondary navigation (bottom)
  const secondaryNavigation = [
    { name: 'Integrations', href: '/integrations', icon: Link2 },
    { name: 'Settings', href: '/settings', icon: Settings },
  ]

  const toggleSubmenu = (menuName: string) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }))
  }

  const handleLogout = () => {
    logout()
  }

  // Render navigation item
  const renderNavItem = (item: any, isMobile: boolean = false) => {
    const hasSubmenu = item.submenu && item.submenu.length > 0
    const isExpanded = expandedMenus[item.name.toLowerCase().replace(/ & /g, '-').replace(/ /g, '-')]

    if (hasSubmenu) {
      return (
        <div key={item.name}>
          <button
            onClick={() => toggleSubmenu(item.name.toLowerCase().replace(/ & /g, '-').replace(/ /g, '-'))}
            className={`group flex items-center justify-between w-full rounded-md px-3 py-2 text-sm font-medium transition-colors duration-150 ${
              item.current
                ? 'bg-white/[0.06] text-white border-l-2 border-primary-400'
                : 'text-white/50 hover:text-white/70 hover:bg-white/[0.04] border-l-2 border-transparent'
            }`}
          >
            <div className="flex items-center">
              <item.icon
                className={`mr-3 h-5 w-5 flex-shrink-0 ${
                  item.current ? 'text-white' : 'text-white/40 group-hover:text-white/60'
                }`}
              />
              <span className="truncate">{item.name}</span>
            </div>
            <ChevronDown
              className={`h-4 w-4 text-white/40 transition-transform duration-150 flex-shrink-0 ${
                isExpanded ? 'rotate-180' : ''
              }`}
            />
          </button>
          {isExpanded && (
            <div className="ml-4 mt-1 space-y-0.5">
              {item.submenu.map((subItem: any) => (
                <Link
                  key={subItem.name}
                  to={subItem.href}
                  className={`group flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors duration-150 ${
                    location.pathname === subItem.href || location.pathname.startsWith(subItem.href + '/')
                      ? 'bg-white/[0.06] text-white border-l-2 border-primary-400'
                      : 'text-white/40 hover:text-white/60 hover:bg-white/[0.04] border-l-2 border-transparent'
                  }`}
                  onClick={() => isMobile && setSidebarOpen(false)}
                >
                  <subItem.icon
                    className={`mr-3 h-4 w-4 flex-shrink-0 ${
                      location.pathname === subItem.href || location.pathname.startsWith(subItem.href + '/')
                        ? 'text-white'
                        : 'text-white/30 group-hover:text-white/50'
                    }`}
                  />
                  <span className="truncate">{subItem.name}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      )
    }

    return (
      <Link
        key={item.name}
        to={item.href}
        className={`group flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors duration-150 ${
          item.current
            ? 'bg-white/[0.06] text-white border-l-2 border-primary-400'
            : 'text-white/50 hover:text-white/70 hover:bg-white/[0.04] border-l-2 border-transparent'
        }`}
        onClick={() => isMobile && setSidebarOpen(false)}
      >
        <item.icon
          className={`mr-3 h-5 w-5 flex-shrink-0 ${
            item.current ? 'text-white' : 'text-white/40 group-hover:text-white/60'
          }`}
        />
        <span className="truncate">{item.name}</span>
      </Link>
    )
  }

  return (
    <div className="min-h-screen bg-[#080F14]">
      {/* Mobile sidebar */}
      <div
        className={`fixed inset-0 z-50 lg:hidden ${
          sidebarOpen ? 'block' : 'hidden'
        }`}
      >
        <div
          className="fixed inset-0 bg-black/60"
          onClick={() => setSidebarOpen(false)}
        />
        <div className="fixed inset-y-0 left-0 flex w-full max-w-xs flex-col bg-[#0C1319] border-r border-white/[0.06]">
          <div className="flex h-16 items-center justify-between px-4 border-b border-white/[0.06]">
            <div className="flex items-center space-x-2">
              <img src="/logo192.png" alt="Synap" className="h-8 w-8" />
              <h1 className="text-xl font-bold text-white">
                Synap
              </h1>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarOpen(false)}
              className="text-white/50 hover:text-white hover:bg-white/[0.04]"
            >
              <X size={20} />
            </Button>
          </div>
          <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
            {/* Primary Navigation */}
            {primaryNavigation.map((item) => renderNavItem(item, true))}

            {/* Divider */}
            <div className="pt-4 pb-2">
              <div className="border-t border-white/[0.06]" />
            </div>

            {/* Secondary Navigation */}
            <div className="space-y-1">
              {secondaryNavigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors duration-150 ${
                    isPathActive(item.href)
                      ? 'bg-white/[0.06] text-white border-l-2 border-primary-400'
                      : 'text-white/40 hover:text-white/60 hover:bg-white/[0.04] border-l-2 border-transparent'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon
                    className={`mr-3 h-5 w-5 flex-shrink-0 ${
                      isPathActive(item.href) ? 'text-white' : 'text-white/30 group-hover:text-white/50'
                    }`}
                  />
                  <span className="truncate">{item.name}</span>
                </Link>
              ))}
            </div>
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex min-h-0 flex-1 flex-col bg-[#0C1319] border-r border-white/[0.06]">
          <div className="flex h-16 items-center px-4 border-b border-white/[0.06]">
            <img src="/logo192.png" alt="Synap" className="h-8 w-8 mr-2" />
            <div className="flex items-baseline">
              <h1 className="text-xl font-bold text-white">
                Synap
              </h1>
              <span className="ml-2 text-xs font-medium text-white/40 uppercase tracking-wider">
                Marketing OS
              </span>
            </div>
          </div>
          <div className="flex flex-1 flex-col overflow-y-auto">
            <nav className="flex-1 px-3 py-4 space-y-1">
              {/* Primary Navigation */}
              {primaryNavigation.map((item) => renderNavItem(item))}
            </nav>

            {/* Secondary Navigation */}
            <div className="border-t border-white/[0.06] px-3 py-4 space-y-1">
              <p className="px-3 text-[11px] font-semibold text-white/30 uppercase tracking-wider">
                Configuration
              </p>
              {secondaryNavigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors duration-150 ${
                    isPathActive(item.href)
                      ? 'bg-white/[0.06] text-white border-l-2 border-primary-400'
                      : 'text-white/40 hover:text-white/60 hover:bg-white/[0.04] border-l-2 border-transparent'
                  }`}
                >
                  <item.icon
                    className={`mr-3 h-5 w-5 flex-shrink-0 ${
                      isPathActive(item.href) ? 'text-white' : 'text-white/30 group-hover:text-white/50'
                    }`}
                  />
                  <span className="truncate">{item.name}</span>
                </Link>
              ))}
            </div>

            {/* Agency and user info */}
            <div className="border-t border-white/[0.06] p-4">
              {user?.agency && (
                <div className="mb-4 rounded-lg bg-white/[0.04] p-3 border border-white/[0.06]">
                  <div className="flex items-center">
                    <Building className="h-5 w-5 text-white/50 mr-2 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-white truncate">
                        {user.agency.name}
                      </p>
                      <p className="text-xs text-white/40 capitalize">
                        {user.agency.plan} Plan
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* User Menu Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center w-full rounded-md hover:bg-white/[0.04] p-2 transition-colors duration-150"
                >
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 flex items-center justify-center">
                      <User className="h-4 w-4 text-white" />
                    </div>
                  </div>
                  <div className="ml-3 flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium text-white truncate">
                      {user?.email}
                    </p>
                    <p className="text-xs text-white/40 capitalize">
                      {user?.role?.replace('_', ' ')}
                    </p>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-white/40 transition-transform duration-150 flex-shrink-0 ${showUserMenu ? 'rotate-180' : ''}`} />
                </button>

                {/* User dropdown menu */}
                {showUserMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowUserMenu(false)}
                    />
                    <div className="absolute bottom-full left-0 right-0 mb-2 bg-[#0C1319] rounded-lg border border-white/[0.06] z-50">
                      <div className="p-2">
                        <button
                          onClick={() => {
                            handleLogout()
                            setShowUserMenu(false)
                          }}
                          className="flex items-center w-full px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 rounded-md transition-colors duration-150"
                        >
                          <LogOut className="h-4 w-4 mr-3" />
                          Logout
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-40 bg-[#0C1319] border-b border-white/[0.06]">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
            {/* Left: Mobile menu */}
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden text-white/50 hover:text-white hover:bg-white/[0.04]"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu size={20} />
            </Button>

            {/* Center-Left: Agency and Brand selectors */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:space-x-3">
              <AgencySelector />
              <div className="hidden sm:block h-6 w-px bg-white/[0.06]" />
              <BrandSelector />
              {selectedBrand && (
                <div className="flex items-center px-3 py-1.5 bg-blue-500/10 border border-blue-400/20 rounded-md max-w-[200px] sm:max-w-none">
                  <Building className="h-4 w-4 text-blue-300 mr-2 flex-shrink-0" />
                  <span className="text-xs sm:text-sm font-medium text-white truncate">
                    {selectedBrand.name}
                  </span>
                  {selectedBrand.industry && (
                    <span className="ml-2 text-xs text-blue-300/70 hidden sm:inline">
                      • {selectedBrand.industry}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Right: Connection status, Notifications, Decisions and user */}
            <div className="flex items-center space-x-4">
              {/* Real-time connection indicator */}
              <ConnectionStatus />

              {/* Tier Badge */}
              {tierData && (
                <div className="hidden sm:block">
                  <TierBadge tier={tierData.tier} size="small" />
                </div>
              )}

              {/* Decision Queue Badge */}
              <DecisionBadge
                userId={userId ?? 'anonymous'}
                onClick={() => window.location.href = '/decisions'}
                className="cursor-pointer hover:text-orange-400 transition-colors duration-150 text-white"
              />

              {/* Notification Bell */}
              <NotificationBell
                userId={userId ?? 'anonymous'}
                onClick={() => setShowNotifications(!showNotifications)}
                className="cursor-pointer hover:text-blue-400 transition-colors duration-150 text-white"
              />

              <div className="lg:hidden flex items-center">
                <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 flex items-center justify-center">
                  <User className="h-4 w-4 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Notification Center Dropdown */}
          {showNotifications && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowNotifications(false)}
              />
              <div className="absolute top-full right-4 sm:right-6 lg:right-8 mt-2 z-50 w-full max-w-md">
                <NotificationCenter
                  userId={userId ?? 'anonymous'}
                  maxHeight="500px"
                  showFilters={true}
                  onNotificationClick={() => setShowNotifications(false)}
                />
              </div>
            </>
          )}
        </header>

        {/* Quota Warning Banner - Story 3.2 */}
        <QuotaWarningBanner />

        {/* Page content */}
        <main className="flex-1">
          <div className="py-6">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <Outlet />
            </div>
          </div>
        </main>

        {/* Feedback Widget - Floating sidebar */}
        <FeedbackWidget />
      </div>

      {/* Real-time notification toasts */}
      <NotificationToast userId={userId ?? 'anonymous'} enabled={true} playSound={false} />

      {/* Quota Warning Modal (95%) - Story 3.3 */}
      <QuotaWarningModal />

      {/* Hard Stop Paywall (100%) - Story 3.4 */}
      <QuotaExceededPaywall />

      {/* Beta Feedback Modal (7 days after invite) - Story 5.4 */}
      {shouldShowBetaFeedback && user?.id && (
        <BetaFeedbackModal
          open={shouldShowBetaFeedback}
          onClose={dismissBetaFeedback}
          userId={user.id}
        />
      )}
    </div>
  )
}
